/* eslint-disable no-console */

export type Properties = Record<string, unknown>;

export type AnalyticsSource =
	| 'Items Overview Page'
	| 'Item Detail Page'
	| 'Kits Overview Page'
	| 'Kit Detail Page'
	| 'Reservations Overview Page'
	| 'Reservation Detail Page'
	| 'Check-outs Overview Page'
	| 'Check-out Detail Page'
	| 'Users Overview Page'
	| 'User Detail Page'
	| 'Category Select'
	| 'User Select'
	| 'Location Select'
	| 'Check In'
	| 'Calendar'
	| 'Main Navigation'
	| 'Quick Checkin Modal'
	| 'Quick Add Menu'
	| 'Quick Book Panel'
	| 'Account Setup Page'
	| 'New User Page'
	| 'Items Import Page'
	| 'Reservation Picking Page'
	| 'Reports Detail Page'
	| `Reports Overview Page`
	| 'Account Manage Page'
	| 'Manage Organisation Page'
	| 'User Groups Detail Page'
	| 'User Groups Overview Page'
	| 'Permissions Settings Page'
	| 'General Settings Page'
	| 'Item Type Detail Page'
	| 'Location Detail Page'
	| 'Locations Overview Page'
	| 'Notifications Settings Page'
	| 'Opening Hours Settings Page'
	| 'Settings Overview Page'
	| 'Subscription Review Page'
	| 'Spotchecks Overview Page'
	| 'Categories Settings Page'
	| 'New Item Page'
	| 'Ticket Detail Page'
	| 'Tickets Overview Page'
	| `${string} Types Settings Page`
	| `${string} Overview Page`
	| `${string} Felicitation Modal`
	| `${string} Date Range Field`;

// Properties to send to all analytics services
export interface WorkspaceAnalyticsProperties {
	organisationId: string;
	workspaceId: string;
	workspaceName: string;
	industry: string;
	registrationDate: string;
	conversionDate: string;
	creationDate: string;
	renewalDate: string;
	recurlyAccountCode: string;
	itemQuota: number;
	itemQuotaPercentage: number;
	progress: number;
	subscriptionPlan: string;
	billingPeriod: string;
	isInBetaProgram: boolean;
	featureFlags: string[];
	availableFeatures: Properties;
	enabledFeatures: Properties;
	tags: string[];
}

export interface UserAnalyticsProperties {
	userId: string;
	name: string;
	email: string;
	isAccountOwner: boolean;
	isAdministrator: boolean;
	isSyncedUser: boolean;
	canLoginWithSSO: boolean;
	screenWidth: string;
	screenHeight: string;
	userTimezone: string;
	seatType: 'admin' | 'user';
	userRole: string;
}

export type AnalyticsProperties = WorkspaceAnalyticsProperties & UserAnalyticsProperties;

export interface PluginConfig {
	apiKey: string;
}

export interface AnalyticsPlugin {
	name: string;
	identify(props: AnalyticsProperties): void;
	initialize(): void;
	page(event: string): void;
	track(event: string, source: string, props?: Properties): void;
}

const shouldDebug = process.env.DEBUG_TRACKING === 'true';

export class Analytics {
	private initializedPlugins: AnalyticsPlugin[] = [];
	private isInitialized = false;

	constructor(private readonly plugins: AnalyticsPlugin[]) {}

	destroy(): void {
		this.initializedPlugins = [];
		this.isInitialized = false;
	}

	identify(props: AnalyticsProperties): void {
		if (!this.isInitialized) this.initialize();

		this.initializedPlugins.forEach((plugin) => {
			try {
				plugin.identify(props);
			} catch (error) {
				console.warn(`Could not identify ${plugin.name}`, error);
			}
		});
	}

	initialize(): void {
		this.plugins.forEach((plugin) => {
			try {
				plugin.initialize();

				if (shouldDebug) console.warn(`Plugin ${plugin.name} has been initialized`);

				this.initializedPlugins.push(plugin);
			} catch (error) {
				console.warn(`Could not initialize ${plugin.name}`, error);
			}
		});

		this.isInitialized = true;
	}

	page(event: string): void {
		if (!this.isInitialized) this.initialize();

		if (shouldDebug) console.warn(`Tracking page ${event}`);

		this.initializedPlugins.forEach((plugin) => {
			try {
				plugin.page(event);
			} catch (error) {
				console.warn(`Plugin ${plugin.name} could not track page`, error);
			}
		});
	}

	track(event: string, source: AnalyticsSource, props?: Properties): void {
		if (!this.isInitialized) this.initialize();

		if (shouldDebug) console.warn(`Tracking ${event} from ${source} with props ${JSON.stringify(props)}`);

		this.initializedPlugins.forEach((plugin) => {
			try {
				plugin.track(event, source, props);
			} catch (error) {
				console.warn(`Plugin ${plugin.name} could not track event ${event}`, error);
			}
		});
	}
}
